// Carer-parity S3a — minimal layout wrapper for authenticated carer
// routes; provides the shared mobile-friendly body container so individual
// pages don't repeat the slate-50 + min-h-screen scaffolding.
//
// Carer-parity S3b — owns the carer-side notification surface end-to-end:
//   * Notifications query via useFetcher() → carerFetch (silent refresh)
//   * useWebSocket subscriber invalidates the query on push events
//     (Sub-7 = 7a — WS-driven; the 30s refetchInterval mirrors Shell's
//     belt-and-braces fallback)
//   * <NotificationsDrawer> mounted at layout root with shared open state
//   * CarerNotificationsContext exposes openDrawer + items + unreadCount
//     to the bell rendered in each page's header (CarerNotificationBell)
//
// Header chrome remains page-owned (CarerTopBar on the report page,
// inline navy-800 headers on CarerHomePage / CarerVisitDetailPage). Folding
// those into a single CarerTopBar is a follow-up beyond S3b.

import { useCallback, useMemo, useState, type JSX } from "react";
import { Outlet } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFetcher } from "../../hooks/useFetcher.js";
import { useWebSocket, type WsEvent } from "../../hooks/useWebSocket.js";
import {
  NotificationsDrawer, type Notification,
} from "../../components/NotificationsDrawer.js";
import {
  CarerNotificationsProvider, type CarerNotificationsContextValue,
} from "./CarerNotificationsContext.js";

interface NotificationsResponse {
  data: Notification[];
  total: number;
  page: number;
  limit: number;
}

const NOTIFICATIONS_QUERY_KEY = ["carer-notifications"] as const;

export function CarerLayout(): JSX.Element {
  const fetcher = useFetcher();
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data } = useQuery<NotificationsResponse>({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: () => fetcher<NotificationsResponse>("/notifications?limit=20&unread_only=false"),
    refetchInterval: 30_000,
  });

  // Sub-7 = 7a — WS-driven invalidation. Any inbound notification push
  // refetches the carer's drawer feed so the bell badge updates without
  // waiting for the 30s polling tick.
  const handleWsEvent = useCallback((_ev: WsEvent): void => {
    void qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
  }, [qc]);
  useWebSocket(handleWsEvent);

  const items = data?.data ?? [];
  const unreadCount = items.filter((n) => n.read_at === null).length;

  const ctx = useMemo<CarerNotificationsContextValue>(() => ({
    items,
    unreadCount,
    drawerOpen,
    openDrawer: () => setDrawerOpen(true),
    closeDrawer: () => setDrawerOpen(false),
  }), [items, unreadCount, drawerOpen]);

  return (
    <CarerNotificationsProvider value={ctx}>
      <div className="min-h-screen bg-slate-50" data-testid="carer-layout">
        <Outlet />
      </div>
      <NotificationsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        items={items}
      />
    </CarerNotificationsProvider>
  );
}
